// components/shared/LookupLinks.js — the address of a found property, and "Look it up on:".
//
// Owner, 25 Oct 2026: the address used to open only the page the property was found on —
// nearly always a CommercialCafe building page that shows no space. Now the address opens the
// listing when there is one, a building page is labelled as such, and every address has a row
// of one-click lookups on the other sites (LoopNet, Crexi, CityFeet, CommercialCafe for a
// business; Apartments.com, Realtor.com, Zillow, ForRent for homes), the other set under
// "more". The links are built in utils/listingLinks.js; nothing here fetches anything.
import React from 'react';
import { primaryLink, lookupLinks, townSearchLinks, siteKindFor } from '../../utils/listingLinks';
import { siteFromDocument } from '../../utils/siteBrand';

/** The state for an address that carries none: the site this is (Illinois / Florida). */
export const siteState = () => (typeof document !== 'undefined' && siteFromDocument(document)) || 'IL';

const stop = (e) => e.stopPropagation();
const A = ({ href, children, title, className = 'text-blue-700 hover:underline' }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={stop} title={title} className={className}>{children}</a>
);

/** The address as a link, per utils/listingLinks primaryLink. */
export const AddressLink = ({ c, className = 'text-blue-700 underline decoration-blue-300 hover:decoration-blue-700' }) => {
    const link = primaryLink(c, siteState());
    return <A href={link.href} title={link.label} className={className}>{c.address}</A>;
};

/** The small line under the address: what the link is ("Listing on LoopNet", "Building page …"). */
export const LinkNote = ({ c, className = 'text-[10px] text-gray-500' }) => {
    const link = primaryLink(c, siteState());
    return (
        <p className={`${className} ${link.kind === 'building' ? 'text-amber-700' : ''}`}>
            {link.label}
            {link.foundOn && <> · <A href={link.foundOn.href}>{link.foundOn.label}</A></>}
        </p>
    );
};

/**
 * "Look it up on: LoopNet · Crexi · CityFeet · CommercialCafe · more"
 * uses: the search's use(s) (decides commercial vs residential); intent: lease / purchase.
 */
const LookupLinks = ({ c, uses, intent, className = '' }) => {
    if (!c || !c.address) return null;
    const kind = siteKindFor(uses);
    const state = siteState();
    const { primary, more } = lookupLinks(c, { kind, state });
    const towns = townSearchLinks(c, { kind, state, intent });
    return (
        <div className={`text-[10px] leading-snug text-gray-500 print:hidden ${className}`}>
            <span>Look it up on: </span>
            {primary.map((l, i) => (
                <React.Fragment key={l.key}>{i > 0 && ' · '}<A href={l.href} title={`Search ${l.label} for this address`}>{l.label}</A></React.Fragment>
            ))}
            {(more.length > 0 || towns.length > 0) && (
                <details className="inline" onClick={stop}>
                    <summary className="ml-1 inline cursor-pointer text-gray-500 hover:text-gray-700">more</summary>
                    <span className="block">
                        {more.map((l, i) => (
                            <React.Fragment key={l.key}>{i > 0 && ' · '}<A href={l.href} title={`Search ${l.label} for this address`}>{l.label}</A></React.Fragment>
                        ))}
                    </span>
                    {towns.length > 0 && (
                        <span className="block">
                            Whole town{(c.municipality || c.town) ? ` (${c.municipality || c.town})` : ''}:{' '}
                            {towns.map((l, i) => (
                                <React.Fragment key={l.key}>{i > 0 && ' · '}<A href={l.href}>{l.label}</A></React.Fragment>
                            ))}
                        </span>
                    )}
                </details>
            )}
        </div>
    );
};

export default LookupLinks;
